import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ArticleCard from "@/components/base/ArticleCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";
import { usePageMeta } from "@/hooks/usePageMeta";

const ARCHIVE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "复盘档案库 - 深夜黑料",
  description: "深夜黑料全部事件复盘档案，支持按类型筛选与关键词检索。",
  inLanguage: "zh-CN",
};

export default function Archive() {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get("category") ?? "all";
  const queryParam = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(queryParam);

  const filtered = useMemo(() => {
    const q = queryParam.trim().toLowerCase();
    return articles.filter((item) => {
      const matchCategory = categoryParam === "all" || item.categoryId === categoryParam;
      const matchQuery =
        !q ||
        item.title.toLowerCase().includes(q) ||
        item.summary.toLowerCase().includes(q) ||
        item.tags.some((tag) => tag.toLowerCase().includes(q));
      return matchCategory && matchQuery;
    });
  }, [categoryParam, queryParam]);

  usePageMeta({
    title: "复盘档案库 | 深夜黑料 事件时间线全收录",
    description:
      "深夜黑料复盘档案库，收录全部事件的时间线梳理与前后因果复盘，支持按综艺、影视、音乐、直播、合约等类型筛选与关键词检索。",
    keywords: "深夜黑料复盘,深夜黑料考古,事件时间线,旧账重提,深夜黑料档案",
    path: "/archive",
    jsonLd: ARCHIVE_JSONLD,
  });

  const updateParams = (next: { category?: string; q?: string }) => {
    const params = new URLSearchParams(searchParams);
    if (next.category !== undefined) {
      if (next.category === "all") params.delete("category");
      else params.set("category", next.category);
    }
    if (next.q !== undefined) {
      if (!next.q) params.delete("q");
      else params.set("q", next.q);
    }
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-14">
      <Breadcrumb items={[{ label: "复盘档案库" }]} />

      <header className="mt-6 max-w-3xl">
        <p className="flex items-center gap-3 text-xs tracking-[0.32em] text-accent-500">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary-500" />
          FULL ARCHIVE
        </p>
        <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
          复盘档案库
        </h1>
        <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
          这里收录全部已归档的事件复盘。每一篇都附完整时间线，按类型筛选或直接搜索关键词，把你想补的那一段旧账找出来。
        </p>
      </header>

      <div className="mt-8 rounded-lg border border-background-200/70 bg-background-100 p-4 md:p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => updateParams({ category: "all" })}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors duration-200 cursor-pointer ${
                categoryParam === "all"
                  ? "bg-primary-600 text-foreground-950"
                  : "border border-background-300/70 text-foreground-700 hover:border-accent-600 hover:text-accent-400"
              }`}
            >
              全部
            </button>
            {categories.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => updateParams({ category: item.id })}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors duration-200 cursor-pointer ${
                  categoryParam === item.id
                    ? "bg-primary-600 text-foreground-950"
                    : "border border-background-300/70 text-foreground-700 hover:border-accent-600 hover:text-accent-400"
                }`}
              >
                {item.name}
              </button>
            ))}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              updateParams({ q: keyword.trim() });
            }}
            className="relative w-full lg:w-72"
            role="search"
          >
            <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-base text-foreground-500" />
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索标题、摘要或标签"
              aria-label="搜索复盘档案"
              className="w-full rounded-md border border-background-300/70 bg-background-50 py-2.5 pl-10 pr-4 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none transition-colors duration-200 focus:border-accent-500"
            />
          </form>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-foreground-600">
          共找到 <strong className="font-semibold text-accent-400">{filtered.length}</strong> 篇复盘
          {categoryParam !== "all" && (
            <span className="ml-2 text-foreground-500">
              · {categories.find((c) => c.id === categoryParam)?.name}
            </span>
          )}
          {queryParam && <span className="ml-2 text-foreground-500">· 关键词「{queryParam}」</span>}
        </p>
        {(categoryParam !== "all" || queryParam) && (
          <button
            type="button"
            onClick={() => {
              setKeyword("");
              updateParams({ category: "all", q: "" });
            }}
            className="flex items-center gap-1 whitespace-nowrap text-sm text-foreground-500 transition-colors duration-200 hover:text-accent-400 cursor-pointer"
          >
            <i className="ri-refresh-line text-base" />
            清除筛选
          </button>
        )}
      </div>

      {filtered.length > 0 ? (
        <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((item) => (
            <ArticleCard key={item.id} article={item} />
          ))}
        </div>
      ) : (
        <div className="mt-6 flex flex-col items-center rounded-lg border border-dashed border-background-300/70 bg-background-100 px-6 py-16 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200/70 text-foreground-500">
            <i className="ri-file-search-line text-2xl" />
          </span>
          <p className="mt-4 font-heading text-base font-semibold text-foreground-900">
            没有找到匹配的档案
          </p>
          <p className="mt-2 max-w-sm text-sm text-foreground-600">
            换一个关键词试试，或清除筛选条件查看全部已归档的事件复盘。
          </p>
          <button
            type="button"
            onClick={() => {
              setKeyword("");
              updateParams({ category: "all", q: "" });
            }}
            className="mt-5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-foreground-950 transition-colors duration-200 hover:bg-primary-500 cursor-pointer"
          >
            查看全部档案
          </button>
        </div>
      )}
    </div>
  );
}